"use client";

import { useState } from "react";
import { Plus, Filter, Shuffle } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import type { StepCatalogEntry, StepCategory } from "@/lib/types/routing";
import { isPlanSufficient, planLabel } from "@/lib/plans";

const CATEGORY_ICONS: Record<StepCategory, typeof Filter> = {
  Filter,
  Transform: Shuffle,
};

interface StepPickerProps {
  catalog: StepCatalogEntry[];
  userPlan: string;
  onAdd: (entry: StepCatalogEntry) => void;
}

export function StepPicker({ catalog, userPlan, onAdd }: StepPickerProps) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);

  const grouped = catalog.reduce<Record<StepCategory, StepCatalogEntry[]>>(
    (acc, entry) => {
      const cat = entry.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(entry);
      return acc;
    },
    {} as Record<StepCategory, StepCatalogEntry[]>
  );

  const categories = Object.keys(grouped) as StepCategory[];

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
      >
        <Plus className="h-3.5 w-3.5" />
        {t("dashboard.routes.stepEditorAddStep")}
      </Button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
          />
          <div className="absolute inset-s-0 top-full z-20 mt-1 w-72 border border-border bg-background shadow-md">
            <div className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
              {t("dashboard.routes.stepPickerTitle")}
            </div>
            {catalog.length === 0 ? (
              <div className="px-3 py-4 text-sm text-muted-foreground">
                {t("dashboard.routes.stepPickerEmpty")}
              </div>
            ) : (
              categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat] ?? Filter;
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-1.5 border-b border-border bg-muted/30 px-3 py-1.5">
                      <Icon className="h-3 w-3 text-muted-foreground" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        {cat}
                      </span>
                    </div>
                    {grouped[cat].map((entry) => {
                      const sufficient = isPlanSufficient(entry.planRequired, userPlan);
                      const label = planLabel(entry.planRequired);
                      return (
                        <button
                          key={entry.type}
                          type="button"
                          className="flex w-full items-center justify-between px-3 py-2 text-start text-sm hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => {
                            onAdd(entry);
                            setOpen(false);
                          }}
                          disabled={!sufficient}
                        >
                          <span>{entry.type}</span>
                          {!sufficient && label && (
                            <span className="font-mono text-[9px] uppercase tracking-wider border border-warning/40 text-warning px-1 py-0.5">
                              {label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
